// src/pages/Community.tsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";
import {
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  MessageCircle,
  Calendar,
  Users,
  Heart,
  Lightbulb,
  Ban,
} from "lucide-react";

const Community: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();

  const socialPlatforms = [
    {
      name: "LinkedIn",
      handle: "@FindWorkOfficial",
      description:
        "Professional networking, career tips, and industry insights",
      icon: Linkedin,
      url: "http://www.linkedin.com/in/find-work-261a53393",
      followers: "25K+",
      posts: "Daily career advice",
    },
    {
      name: "Twitter",
      handle: "@FindWorkApp",
      description: "Real-time job alerts, hiring trends, and quick tips",
      icon: Twitter,
      url: "https://x.com/home",
      followers: "18K+",
      posts: "Multiple times daily",
    },
    {
      name: "Facebook",
      handle: "FindWorkCommunity",
      description: "Community discussions, events, and success stories",
      icon: Facebook,
      url: "https://www.facebook.com/profile.php?id=61584796086016",
      followers: "32K+",
      posts: "Daily updates",
    },
    {
      name: "Instagram",
      handle: "@FindWorkVisual",
      description:
        "Visual career inspiration, workplace tips, and behind-the-scenes",
      icon: Instagram,
      url: "https://www.instagram.com/findworkke/",
      followers: "15K+",
      posts: "Daily stories & posts",
    },
    {
      name: "YouTube",
      handle: "FindWork TV",
      description: "Interview tips, resume workshops, and career webinars",
      icon: Youtube,
      url: "https://www.youtube.com/account",
      followers: "12K+",
      subscribers: "12K+",
      posts: "Weekly videos",
    },
    {
      name: "Discord",
      handle: "FindWork Hub",
      description: "Real-time chat, networking, and community support",
      icon: MessageCircle,
      url: "https://discord.gg/FindWork",
      followers: "8K+",
      posts: "24/7 active",
    },
  ];

  const communityEvents = [
    {
      title: "Weekly Career Q&A",
      date: "Every Wednesday, 7 PM EST",
      platform: "LinkedIn Live",
      description: "Get your career questions answered by industry experts",
      icon: Users,
    },
    {
      title: "Resume Review Workshop",
      date: "First Saturday of each month",
      platform: "Zoom",
      description: "Live resume critiques and improvement tips",
      icon: MessageCircle,
    },
    {
      title: "Tech Hiring Panel",
      date: "October 25, 2024 - 6 PM EST",
      platform: "YouTube Live",
      description: "Learn from tech recruiters at top companies",
      icon: Youtube,
    },
    {
      title: "Networking Mixer",
      date: "November 12, 2024 - 8 PM EST",
      platform: "Discord",
      description: "Virtual networking with professionals in your industry",
      icon: Users,
    },
  ];

  const successStories = [
    {
      name: "Sarah Chen",
      role: "Software Engineer at Google",
      story: "Found my dream job through FindWork community connections",
      platform: "LinkedIn",
      icon: Linkedin,
    },
    {
      name: "Marcus Johnson",
      role: "Marketing Director at Nike",
      story: "Networked with hiring managers through our Twitter community",
      platform: "Twitter",
      icon: Twitter,
    },
    {
      name: "Priya Patel",
      role: "Product Manager at Microsoft",
      story: "Landed multiple interviews through Discord networking events",
      platform: "Discord",
      icon: MessageCircle,
    },
  ];

  const communityGuidelines = [
    {
      title: "Be Respectful",
      description: "Treat all members with respect and professionalism",
      icon: Heart,
    },
    {
      title: "Share Knowledge",
      description: "Help others by sharing your experiences and insights",
      icon: Lightbulb,
    },
    {
      title: "No Spam",
      description: "Keep discussions relevant and avoid self-promotion",
      icon: Ban,
    },
  ];

  const handleBackToHelp = () => {
    navigate("/help-center");
  };

  const handleContactSupport = () => {
    navigate("/contact-support");
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Community - Connect with Workers & Employers | FindWork</title>
        <meta
          name="description"
          content="Join the FindWork community in Kenya. Share experiences, get advice, and connect with workers and employers near you."
        />
        <link rel="canonical" href="https://findwork.ltd/community" />
      </Helmet>
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <button
              onClick={handleBackToHelp}
              className={`mr-4 p-2 rounded-lg transition-colors ${
                isDark
                  ? 'hover:bg-gray-800 text-gray-400 hover:text-white'
                  : 'hover:bg-blue-100 text-blue-600 hover:text-blue-800'
              }`}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1
              className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${
                isDark
                  ? 'from-blue-400 to-blue-600 text-white'
                  : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
              }`}
            >
              Join Our Community
            </h1>
          </div>
          <p
            className={`text-lg max-w-2xl mx-auto ${
              isDark ? 'text-gray-400' : 'text-blue-700'
            }`}
          >
            Connect with fellow job seekers, industry experts, and hiring
            managers across our social platforms
          </p>
        </div>

        {/* Social Platforms Grid */}
        <div className="mb-16">
          <h2
            className={`text-2xl font-bold mb-8 text-center ${
              isDark ? 'text-white' : 'text-blue-800'
            }`}
          >
            Follow Us On Social Media
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {socialPlatforms.map((platform, index) => {
              const IconComponent = platform.icon;
              return (
                <div
                  key={index}
                  className={`rounded-xl p-6 transition-all duration-200 group cursor-pointer ${
                    isDark
                      ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750 hover:border-blue-500/50'
                      : 'bg-white border border-blue-200 shadow-lg hover:shadow-xl hover:shadow-blue-200 hover:border-blue-300'
                  }`}
                  onClick={() => handleExternalLink(platform.url)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`p-2 rounded-lg ${
                          isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                        }`}
                      >
                        <IconComponent
                          className={`w-6 h-6 ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}
                        />
                      </div>
                      <div>
                        <h3
                          className={`text-xl font-semibold ${
                            isDark ? 'text-white' : 'text-blue-800'
                          }`}
                        >
                          {platform.name}
                        </h3>
                        <p
                          className={`text-sm ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}
                        >
                          {platform.handle}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`p-2 rounded-lg ${
                        isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                      }`}
                    >
                      <svg
                        className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                  </div>

                  <p
                    className={`mb-4 ${
                      isDark ? 'text-gray-400' : 'text-blue-700'
                    }`}
                  >
                    {platform.description}
                  </p>

                  <div
                    className={`flex justify-between items-center p-3 rounded-lg ${
                      isDark ? 'bg-gray-700/50' : 'bg-blue-50'
                    }`}
                  >
                    <div>
                      <div
                        className={`text-sm font-medium ${
                          isDark ? 'text-gray-300' : 'text-blue-800'
                        }`}
                      >
                        {platform.name === 'YouTube'
                          ? 'Subscribers'
                          : 'Followers'}
                      </div>
                      <div
                        className={`text-lg font-bold ${
                          isDark ? 'text-white' : 'text-blue-600'
                        }`}
                      >
                        {platform.subscribers || platform.followers}
                      </div>
                    </div>
                    <div>
                      <div
                        className={`text-sm font-medium ${
                          isDark ? 'text-gray-300' : 'text-blue-800'
                        }`}
                      >
                        Posts
                      </div>
                      <div
                        className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-blue-600'
                        }`}
                      >
                        {platform.posts}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Upcoming Events */}
          <div
            className={`rounded-xl p-6 ${
              isDark
                ? 'bg-gray-800 border border-gray-700'
                : 'bg-white border border-blue-200 shadow-lg'
            }`}
          >
            <h2
              className={`text-2xl font-bold mb-6 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              Upcoming Events
            </h2>
            <div className="space-y-4">
              {communityEvents.map((event, index) => {
                const EventIcon = event.icon;
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg transition-colors ${
                      isDark
                        ? 'bg-gray-700/50 hover:bg-gray-700'
                        : 'bg-blue-50 hover:bg-blue-100'
                    }`}
                  >
                    <div className="flex items-start space-x-3 mb-3">
                      <div
                        className={`p-2 rounded-lg ${
                          isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                        }`}
                      >
                        <EventIcon
                          className={`w-5 h-5 ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <h3
                            className={`font-semibold ${
                              isDark ? 'text-white' : 'text-blue-800'
                            }`}
                          >
                            {event.title}
                          </h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              isDark
                                ? 'bg-blue-600 text-white'
                                : 'bg-blue-100 text-blue-600'
                            }`}
                          >
                            {event.platform}
                          </span>
                        </div>
                        <p
                          className={`text-sm mb-2 ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}
                        >
                          <Calendar className="w-4 h-4 inline mr-1" />
                          {event.date}
                        </p>
                        <p
                          className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-blue-700'
                          }`}
                        >
                          {event.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => navigate('/event-calendar')}
              className={`w-full mt-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 ${
                isDark
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span>View Full Event Calendar</span>
            </button>
          </div>

          {/* Success Stories */}
          <div
            className={`rounded-xl p-6 ${
              isDark
                ? 'bg-gray-800 border border-gray-700'
                : 'bg-white border border-blue-200 shadow-lg'
            }`}
          >
            <h2
              className={`text-2xl font-bold mb-6 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              Community Success Stories
            </h2>

            <div className="space-y-4">
              {successStories.map((story, index) => {
                const StoryIcon = story.icon;
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg ${
                      isDark ? 'bg-gray-700/50' : 'bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isDark
                            ? 'bg-blue-600'
                            : 'bg-gradient-to-r from-sky-500 to-blue-600'
                        }`}
                      >
                        <span className="text-white text-sm font-semibold">
                          {story.name
                            .split(' ')
                            .map((n) => n[0])
                            .join('')}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <h3
                            className={`font-semibold ${
                              isDark ? 'text-white' : 'text-blue-800'
                            }`}
                          >
                            {story.name}
                          </h3>
                          <div className="flex items-center space-x-1">
                            <StoryIcon
                              className={`w-4 h-4 ${
                                isDark ? 'text-gray-400' : 'text-blue-500'
                              }`}
                            />
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                isDark
                                  ? 'bg-gray-600 text-gray-300'
                                  : 'bg-blue-100 text-blue-600'
                              }`}
                            >
                              {story.platform}
                            </span>
                          </div>
                        </div>
                        <p
                          className={`text-sm mb-2 ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}
                        >
                          {story.role}
                        </p>
                        <p
                          className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-blue-700'
                          }`}
                        >
                          "{story.story}"
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => handleExternalLink('https://stories.FindWork.com')}
              className={`w-full mt-6 py-3 rounded-lg font-semibold border transition-colors flex items-center justify-center space-x-2 ${
                isDark
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-blue-200 text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Read More Success Stories</span>
            </button>
          </div>
        </div>

        {/* Community Guidelines */}
        <div
          className={`rounded-xl p-8 mb-12 ${
            isDark
              ? 'bg-gradient-to-r from-blue-900/30 to-blue-800/20 border border-blue-700/50'
              : 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-xl'
          }`}
        >
          <h2
            className={`text-2xl font-bold mb-4 text-center ${
              isDark ? 'text-white' : 'text-white'
            }`}
          >
            Community Guidelines
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {communityGuidelines.map((guideline, index) => {
              const GuidelineIcon = guideline.icon;
              return (
                <div key={index} className="p-4">
                  <div
                    className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-blue-600' : 'bg-white/20'
                    }`}
                  >
                    <GuidelineIcon className="w-6 h-6 text-white" />
                  </div>
                  <h3
                    className={`font-semibold mb-2 ${
                      isDark ? 'text-white' : 'text-white'
                    }`}
                  >
                    {guideline.title}
                  </h3>
                  <p
                    className={`text-sm ${
                      isDark ? 'text-gray-300' : 'text-blue-100'
                    }`}
                  >
                    {guideline.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link
            to="/help-center"
            className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
              isDark
                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                : 'bg-white text-blue-600 hover:bg-blue-50 border border-blue-200'
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Back to Help Center</span>
          </Link>
          <button
            onClick={handleContactSupport}
            className={`px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            <span>Contact Support</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Community;
